import { useState, useEffect, useCallback, useRef } from 'react';
import {
  collection,
  collectionGroup,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  onSnapshot,
  query,
  where,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { trackWrite } from '../lib/trackWrite';
import { noteSaveFailure, noteSaveSuccess } from '../lib/saveHealth';

// Walter 2026-10-09: reminder writes report into the SAME save-health
// counter as list saves, so a blocked database raises the existing banner
// instead of failing in silence. A list save goes through a transaction and
// rejects when blocked; these are plain writes, whose promise settles only
// on the server's acknowledgement - under Ghostery they simply hang. The
// deadline in lib/trackWrite is what turns that into a signal. His Ghostery
// call: "see how bad an impact Ghostery has, as the spec tells users won't
// turn it off either" - so the app copes rather than asking them to.
const reportWrite = (promise) => trackWrite(promise, {
  onFailure: noteSaveFailure,
  onSuccess: noteSaveSuccess,
});

/**
 * useReminders — Firestore CRUD + live subscription for all reminders
 * the current user can see (own + on shared lists).
 *
 * Schema layout:
 *   users/{uid}/reminders/{id}
 *
 * Each reminder doc carries a denormalised `aclUids` array (the list
 * owner + its editors + its viewers) so a collectionGroup query with
 * `where('aclUids', 'array-contains', currentUid)` returns every
 * reminder the user has access to — own AND any reminder created by
 * a collaborator on a list shared with them.
 *
 * Walter 2026-05-25 (Bug #12 — round-2 tester report). Before this,
 * the hook queried only `users/{currentUid}/reminders/...` so User 2
 * never saw User 1's reminders on a shared board, and vice versa. See
 * docs/proposals/shared-reminder-sync.md for the design rationale.
 *
 * A reminder doc looks like:
 *   {
 *     text:           string,            // human-readable phrase
 *     parsedDate:     Timestamp | null,  // when (null = undated)
 *     recurrence:     object | null,     // future: rrule-shaped
 *     sourceListId:   string,            // list the line came from
 *     sourceLineHash: string,            // FNV-1a of the original line
 *     aclUids:        string[],          // who can see this — denormalised
 *                                        // from the source list's editors +
 *                                        // viewers + ownerUid at create time
 *     status:         'open' | 'done' | 'snoozed',
 *     createdAt:      serverTimestamp,
 *     updatedAt:      serverTimestamp,
 *   }
 *
 * Lives at the app root (App.js).
 *
 * `reminders` is `[]` while loading and after a load when the user has
 * no reminders. The `loading` flag flips to false after the first
 * snapshot from EITHER subscription resolves.
 *
 * `listsForAcl` (optional) — array of list docs (with `editors`,
 * `viewers`, owner) the caller has loaded. When provided, the hook
 * uses it during legacy-reminder backfill to populate `aclUids`
 * accurately (matching the list's current ACL). When not provided,
 * backfill falls back to `[currentUid]` only — own-visibility is
 * restored, but shared collaborators won't see the backfilled
 * reminder until it's touched again.
 */
export function useReminders(userId, opts = {}) {
  const { listsForAcl } = opts;
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  // Map of reminder id → ownerUid (the user-path the doc lives under).
  // We need this because the collectionGroup query returns docs from
  // other users' subtrees, and update/delete must route writes to the
  // right path. Built from doc.ref.path on every snapshot.
  const ownerByIdRef = useRef(new Map());
  // Track which legacy docs we've already backfilled to avoid loops
  // (the backfill update fires the snapshot listener again).
  const backfilledRef = useRef(new Set());
  // Stash listsForAcl in a ref so the legacy snapshot effect doesn't
  // tear down + re-subscribe every time the lists array reference
  // changes. The backfill reads the current value on-demand.
  const listsForAclRef = useRef(listsForAcl);
  useEffect(() => { listsForAclRef.current = listsForAcl; }, [listsForAcl]);

  // Reset state cleanly on signed-out / user-changed.
  useEffect(() => {
    if (!userId) {
      setReminders([]);
      setLoading(false);
      ownerByIdRef.current = new Map();
      backfilledRef.current = new Set();
    }
  }, [userId]);

  // Helper: extract ownerUid from a Firestore doc path
  // ("users/{uid}/reminders/{id}" → "{uid}").
  const ownerFromPath = (path) => {
    const m = /^users\/([^/]+)\/reminders\//.exec(path || '');
    return m ? m[1] : null;
  };

  // Merge two snapshot batches into the reminders state. We get docs
  // from BOTH the legacy per-user subscription AND the collectionGroup
  // subscription — the same doc may appear in both. Dedupe by id.
  const legacyDocsRef = useRef([]);
  const groupDocsRef = useRef([]);
  const recompute = useCallback(() => {
    const byId = new Map();
    for (const r of legacyDocsRef.current) byId.set(r.id, r);
    // CollectionGroup wins on conflict — it carries the freshest server
    // snapshot for docs visible cross-user too.
    for (const r of groupDocsRef.current) byId.set(r.id, r);
    setReminders(Array.from(byId.values()));
  }, []);

  // Subscription 1 — legacy `users/{currentUid}/reminders/`. Catches
  // reminders that haven't been backfilled with `aclUids` yet (and
  // thus aren't returned by the collectionGroup query below). Doubles
  // as the backfill driver: any doc loaded here without `aclUids`
  // gets one written.
  useEffect(() => {
    if (!userId) return undefined;
    const ref = collection(db, 'users', userId, 'reminders');
    const unsub = onSnapshot(ref, (snap) => {
      const docs = snap.docs.map((d) => {
        ownerByIdRef.current.set(d.id, ownerFromPath(d.ref.path));
        return { id: d.id, ...d.data() };
      });
      legacyDocsRef.current = docs;
      recompute();
      setLoading(false);

      // Backfill aclUids on any of MY docs that lack it. Owner-only
      // writes per the existing rule — we're only touching our own
      // collection here, so this is always permitted.
      for (const r of docs) {
        if (Array.isArray(r.aclUids) && r.aclUids.includes(userId)) continue;
        if (backfilledRef.current.has(r.id)) continue;
        backfilledRef.current.add(r.id);
        const aclUids = computeAclForBackfill(r, userId, listsForAclRef.current);
        const dref = doc(db, 'users', userId, 'reminders', r.id);
        updateDoc(dref, { aclUids, updatedAt: serverTimestamp() })
          .catch((e) => console.warn('[useReminders] backfill aclUids failed:', e));
      }
    }, (err) => {
      console.error('useReminders legacy snapshot error:', err);
      setLoading(false);
    });
    return unsub;
  }, [userId, recompute]);

  // Subscription 2 — collectionGroup, scoped by `aclUids`. Returns every
  // reminder I can see: my own (after backfill) and any on lists shared
  // with me. If the index is still building, this will surface
  // FAILED_PRECONDITION and we log + carry on — Subscription 1 still
  // covers the user's own reminders, so the agenda is never empty.
  useEffect(() => {
    if (!userId) return undefined;
    let unsub = () => {};
    try {
      const q = query(
        collectionGroup(db, 'reminders'),
        where('aclUids', 'array-contains', userId),
      );
      unsub = onSnapshot(q, (snap) => {
        const docs = snap.docs.map((d) => {
          ownerByIdRef.current.set(d.id, ownerFromPath(d.ref.path));
          return { id: d.id, ...d.data() };
        });
        groupDocsRef.current = docs;
        recompute();
        setLoading(false);
      }, (err) => {
        // Most commonly: 'failed-precondition' while the collectionGroup
        // index is still building. Not user-facing — the legacy
        // subscription above keeps things working.
        console.warn('[useReminders] collectionGroup snapshot error (legacy path still active):', err?.code || err);
      });
    } catch (e) {
      console.warn('[useReminders] collectionGroup subscribe threw:', e);
    }
    return () => { try { unsub(); } catch {} };
  }, [userId, recompute]);

  /**
   * createReminder(payload) — adds a reminder doc to the CURRENT user's
   * collection. Caller passes `aclUids` (array of UIDs that should be
   * able to see this reminder); computed from the source list's editors
   * + viewers + ownerUid by the capture-flow caller.
   * Returns the new doc id.
   */
  const createReminder = useCallback(async (payload) => {
    if (!userId) return null;
    const ref = collection(db, 'users', userId, 'reminders');
    const aclUids = Array.isArray(payload.aclUids) && payload.aclUids.length > 0
      ? Array.from(new Set([...payload.aclUids, userId]))
      : [userId];
    // NOTE: this is a WHITELIST - a field the caller sends but that is not
    // named here is dropped silently. Walter 2026-09-26: that is exactly how
    // #246 shipped broken. The capture path passed `startDate`, the popover
    // read nothing back, and his "starting 10/2" vanished on the first edit.
    // Add new reminder fields HERE as well as at the call site.
    const docData = {
      text: payload.text || '',
      parsedDate: payload.parsedDate || null,
      recurrence: payload.recurrence || null,
      // #246 - the day a recurring series was told to start, when he typed
      // one. Never advanced (parsedDate is the next fire and moves on).
      startDate: payload.startDate || null,
      sourceListId: payload.sourceListId || null,
      sourceLineHash: payload.sourceLineHash || null,
      aclUids,
      status: payload.status || 'open',
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    };
    const result = await reportWrite(addDoc(ref, docData));
    return result.id;
  }, [userId]);

  /**
   * updateReminder(id, patch) — partial update. Routes the write to
   * the doc's actual owning user-path (which may be ANOTHER user if
   * the reminder is on a shared list). Firestore rules deny the
   * cross-user write — the UI shouldn't expose "edit" affordances for
   * reminders the current user doesn't own (documented v1 limitation
   * in the shared-reminder-sync proposal).
   */
  const updateReminder = useCallback(async (id, patch) => {
    if (!userId || !id) return;
    const ownerUid = ownerByIdRef.current.get(id) || userId;
    const ref = doc(db, 'users', ownerUid, 'reminders', id);
    await reportWrite(updateDoc(ref, { ...patch, updatedAt: serverTimestamp() }));
  }, [userId]);

  /**
   * deleteReminder(id) — same cross-user routing as updateReminder.
   */
  const deleteReminder = useCallback(async (id) => {
    if (!userId || !id) return;
    const ownerUid = ownerByIdRef.current.get(id) || userId;
    const ref = doc(db, 'users', ownerUid, 'reminders', id);
    await reportWrite(deleteDoc(ref));
  }, [userId]);

  /**
   * findReminderForLine(sourceListId, line) — returns the reminder doc
   * (if any) registered against the same source line. Used by the
   * dedicated-list parser to avoid duplicate inserts on re-save.
   */
  const findReminderForLine = useCallback((sourceListId, line) => {
    const target = hashReminderLine(line);
    return reminders.find(
      (r) => r.sourceListId === sourceListId && r.sourceLineHash === target
    ) || null;
  }, [reminders]);

  return {
    reminders,
    loading,
    createReminder,
    updateReminder,
    deleteReminder,
    findReminderForLine,
  };
}

/**
 * computeAclForBackfill — best-effort `aclUids` for a legacy reminder
 * doc that doesn't have the field yet. If the caller passed
 * `listsForAcl` and we can locate the source list there, we mirror its
 * editors + viewers + ownerUid. Otherwise we fall back to
 * `[currentUid]` — at least the owner sees the reminder via the
 * collectionGroup query; cross-collaborator visibility kicks in once
 * the reminder is next edited (or via a future smart-backfill sweep).
 */
function computeAclForBackfill(reminder, currentUid, listsForAcl) {
  const fallback = [currentUid];
  if (!reminder?.sourceListId) return fallback;
  if (!Array.isArray(listsForAcl) || listsForAcl.length === 0) return fallback;
  const src = listsForAcl.find((l) => l.id === reminder.sourceListId);
  if (!src) return fallback;
  const editors = Array.isArray(src.editors) ? src.editors : [];
  const viewers = Array.isArray(src.viewers) ? src.viewers : [];
  // Owner uid: explicit on shared placements, else the current user
  // (the current user is the owner of any list they see in their own
  // boards collection that isn't a placed shared list).
  const ownerUid = src._sharedFromOwnerUid || currentUid;
  return Array.from(new Set([currentUid, ownerUid, ...editors, ...viewers]));
}

/**
 * computeAclForCreate — helper for capture-flow callers. Determines
 * who can see this reminder via the collectionGroup query in
 * useReminders.
 *
 * PERSONAL model (Walter 2026-05-25 product decision):
 *   Reminders are owned by the user who typed them, even on shared
 *   lists. Only the creator gets agenda entries + push notifications.
 *   Collaborators see the 🔔 line in the shared list's text (it's
 *   shared content), but it's rendered dimmed/grayed (the creator's
 *   client styles bright, every other viewer gets gray) and never
 *   reaches their agenda. See docs/proposals/shared-reminder-sync.md
 *   for the full rationale; gist is: noise control, ownership clarity,
 *   timezone sanity, and "you can always add your own 🔔 if you want
 *   one too."
 *
 * The aclUids array we write is therefore JUST [currentUid] — the
 * single-element form means the collectionGroup query in useReminders
 * only returns the reminder to its creator. The denormalised-ACL
 * infrastructure (this helper, the collectionGroup query, the
 * cross-user write routing in updateReminder/deleteReminder) all
 * stays in place as defensive scaffolding for a future hybrid /
 * group model — see "parked follow-ups" in BACKLOG.md. Today, none
 * of that cross-user routing fires because no reminder has aclUids
 * extending past its owner.
 *
 * `sourceList` arg kept for signature compatibility + future hybrid
 * model; currently unused.
 */
// eslint-disable-next-line no-unused-vars
export function computeAclForCreate(sourceList, currentUid) {
  return [currentUid];
}

/**
 * hashReminderLine — FNV-1a 32-bit hash of the line, hex-encoded.
 *
 * Used as a stable key tying a reminder doc back to the line in the
 * source list it came from. We don't store the literal line text in
 * the reminder doc (it'd grow stale the moment the user edits the
 * source) — the hash is enough to dedupe on re-save.
 *
 * Exported standalone (no hook) so the capture flow can compute it
 * before calling createReminder.
 */
export function hashReminderLine(line) {
  // FNV-1a 32-bit. Good enough for dedupe; not security-sensitive.
  let h = 0x811c9dc5;
  const s = (line || '').trim();
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  // Convert to unsigned 32-bit hex.
  return (h >>> 0).toString(16).padStart(8, '0');
}
